import axios from "axios";
import { FilePenLine } from "lucide-react";
import { toast } from "@/components/ui/sonner";

const API = `${window.location.origin}/api`;

/**
 * I 3 banner di stato dell'OpportunityForm:
 *
 *   1. **Readonly banner** — quando `?readonly=1` (opp eliminata da GHL)
 *      o `?consult=1` (da Chiama Paziente). Sola consultazione + foto.
 *   2. **Stage-lock banner** — quando l'opp è uscita dal primo stage
 *      editabile (es. è in "Caricati"). Per gli admin contiene un toggle
 *      di sblocco persistente (POST /opportunities/{id}/admin-unlock).
 *   3. **Edit-mode banner** — quando si sta modificando un'opp esistente
 *      (URL ha `?opportunityId=...`). Mostra revisione, last update,
 *      bottone "esci dalla modifica".
 *
 * Estratto da App.js il 25/02/2026 nell'ambito del refactoring del
 * componente monolitico. Non ha stato proprio: tutto via props/callback.
 */
export default function OpportunityFormBanners({
  // Readonly state
  isReadonly,

  // Stage-lock state
  stageLockedInfo,
  isAdmin,
  adminUnlock,
  setAdminUnlock,
  editingOpportunityId,

  // Edit-mode state
  effectiveReadonly,
  editingMeta,
  editingLoading,

  // Navigation
  navigate,
  mode,
}) {
  const handleAdminUnlockChange = async (e) => {
    if (!isAdmin) return;
    const enabled = e.target.checked;
    // Optimistic update + persist al backend.
    setAdminUnlock(enabled);
    try {
      await axios.post(
        `${API}/opportunities/${editingOpportunityId}/admin-unlock`,
        { enabled },
        { withCredentials: true }
      );
      toast.success(
        enabled
          ? "Sblocco persistente attivato: anche gli USER potranno modificare questa scheda"
          : "Sblocco persistente rimosso"
      );
    } catch (err) {
      setAdminUnlock(!enabled);
      toast.error(err.response?.data?.detail || "Errore aggiornamento sblocco");
    }
  };

  const handleExitEdit = () => {
    // Se arriva dall'Archivio o dalla Kanban Pipeline, torna lì con lo
    // stato ripristinato. Altrimenti resta in CREATE mode sulla stessa pagina.
    let returned = false;
    try {
      if (sessionStorage.getItem("pianiCuraReturnActive") === "1") {
        sessionStorage.removeItem("pianiCuraReturnActive");
        navigate("/piani-cura");
        returned = true;
      } else if (sessionStorage.getItem("archiveReturnActive") === "1") {
        sessionStorage.removeItem("archiveReturnActive");
        navigate("/opportunita-create");
        returned = true;
      }
    } catch {
      // sessionStorage non disponibile (privacy mode) → fallback al
      // navigate diretto sotto.
    }
    if (!returned) {
      navigate(`/piano-cura-${mode}`, { replace: true });
    }
  };

  return (
    <>
      {/* Read-only / Consult banner (opp deleted from GHL) */}
      {isReadonly && (
        <div
          className="rounded-2xl border-2 border-slate-300 bg-slate-100 p-4 flex items-start gap-3"
          data-testid="readonly-banner"
        >
          <FilePenLine className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 text-sm">
              Solo consultazione del piano e caricamento foto
            </p>
          </div>
        </div>
      )}

      {/* Stage-lock banner: piano già passato allo stage successivo,
          non più modificabile. Mostrato al posto del banner edit/readonly
          quando `stageLockedInfo` è valorizzato e non è un readonly
          "deleted-from-GHL". Nascosto per admin (06/06/2026) — gli admin
          modificano sempre tutto senza vedere alcun banner. */}
      {stageLockedInfo && !isReadonly && !isAdmin && (
        <div
          className="rounded-2xl border-2 border-slate-300 bg-slate-100 p-4 flex items-start gap-3"
          data-testid="stage-locked-banner"
        >
          <FilePenLine className="w-5 h-5 text-slate-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-slate-800 text-sm">
              Piano di cura in sola consultazione
            </p>
            <p className="text-xs text-slate-600 mt-0.5">
              L&apos;opportunità è passata allo stage{" "}
              <span className="font-semibold">
                {stageLockedInfo.stageName || "successivo"}
              </span>
              : il piano non è più modificabile.{" "}
              <span className="font-semibold text-emerald-700">
                Puoi comunque ancora caricare nuove foto.
              </span>
            </p>
            {(isAdmin || adminUnlock) && (
              <label
                className={`mt-3 flex items-center gap-2 select-none ${
                  isAdmin ? "cursor-pointer" : "cursor-default opacity-90"
                }`}
                data-testid="admin-unlock-toggle"
              >
                <input
                  type="checkbox"
                  checked={adminUnlock}
                  disabled={!isAdmin || !editingOpportunityId}
                  onChange={handleAdminUnlockChange}
                  className="w-4 h-4 accent-amber-600 disabled:opacity-60"
                />
                <span
                  className={`text-xs font-bold uppercase tracking-wide ${
                    adminUnlock ? "text-amber-700" : "text-slate-500"
                  }`}
                >
                  {adminUnlock
                    ? "🔓 Modifica abilitata su questa scheda (anche per USER)"
                    : isAdmin
                    ? "🔒 Abilita modifica su questa scheda (solo admin)"
                    : "🔒 Modifica disabilitata su questa scheda"}
                </span>
              </label>
            )}
          </div>
        </div>
      )}

      {/* Edit-mode banner */}
      {editingOpportunityId && !effectiveReadonly && (
        <div
          className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-4 flex items-start gap-3"
          data-testid="edit-mode-banner"
        >
          <FilePenLine className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-amber-900 text-sm">
              Stai modificando l&apos;opportunità #{editingOpportunityId.slice(0, 8)}
              {editingMeta?.revision_count ? ` · revisione ${editingMeta.revision_count}` : ""}
            </p>
            <p className="text-xs text-amber-700 mt-0.5">
              {editingLoading
                ? "Caricamento dati…"
                : "Aggiungi nuove prestazioni al piano già esistente. Le foto possono essere rimosse o aggiunte. Lo stage può essere cambiato."}
            </p>
            {editingMeta?.last_updated_at && !editingLoading && (
              <p className="text-xs text-amber-600 mt-1">
                Ultima modifica:{" "}
                {new Date(editingMeta.last_updated_at).toLocaleString("it-IT", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
                {editingMeta.last_updated_by_email ? ` · ${editingMeta.last_updated_by_email}` : ""}
              </p>
            )}
            {!editingLoading &&
              editingMeta &&
              (editingMeta.medico_name || editingMeta.assicurazione_name) && (
                <p className="text-xs text-amber-600 mt-1">
                  Originale:{" "}
                  {editingMeta.assicurazione_name && <span>📋 {editingMeta.assicurazione_name}</span>}
                  {editingMeta.assicurazione_name && editingMeta.medico_name && <span> · </span>}
                  {editingMeta.medico_name && <span>👨‍⚕️ {editingMeta.medico_name}</span>}
                </p>
              )}
          </div>
          <button
            type="button"
            onClick={handleExitEdit}
            className="text-amber-700 hover:text-amber-900 text-xs underline"
            data-testid="exit-edit-mode"
          >
            esci dalla modifica
          </button>
        </div>
      )}
    </>
  );
}
